import React, { FC, useEffect, useState } from 'react'
import { Modal, Button, Nav, Spinner, Table } from 'react-bootstrap'
import './pmsNestedModalAboveCompleteJob.css'
import { listRiskLibraryCategories, listRiskLibraryTasks } from '../../../QHSE/core/_requests'
import type { RiskLibraryCategory, RiskLibraryTaskSummary } from '../../../QHSE/core/_models'
import { formatRiskAssessmentDisplay, shouldExcludeCriticalEquipment } from './pmsRiskAssessmentPicker'
import type { PmsJobRecord } from '../../core/pms/_models'

type Props = {
  visible: boolean
  onClose: () => void
  job: PmsJobRecord
  onPick: (displayNumber: string) => void
}

export const RiskLibraryBrowseModal: FC<Props> = ({ visible, onClose, job, onPick }) => {
  const excludeCe = shouldExcludeCriticalEquipment(job)
  const [cats, setCats] = useState<RiskLibraryCategory[]>([])
  const [activeCatId, setActiveCatId] = useState<number | null>(null)
  const [tasks, setTasks] = useState<RiskLibraryTaskSummary[]>([])
  const [loadingCats, setLoadingCats] = useState(false)
  const [loadingTasks, setLoadingTasks] = useState(false)

  useEffect(() => {
    if (!visible) return
    setLoadingCats(true)
    listRiskLibraryCategories()
      .then((c) => {
        const filtered = excludeCe ? c.filter((x) => x.code !== 'CRITICAL_EQUIPMENT') : c
        const sorted = [...filtered].sort((a, b) => a.sortOrder - b.sortOrder)
        setCats(sorted)
        setActiveCatId(sorted[0]?.id ?? null)
      })
      .catch(() => setCats([]))
      .finally(() => setLoadingCats(false))
  }, [visible, excludeCe])

  useEffect(() => {
    if (!visible || activeCatId == null) {
      setTasks([])
      return
    }
    setLoadingTasks(true)
    listRiskLibraryTasks(activeCatId, null, job.vesselId ?? null)
      .then(setTasks)
      .catch(() => setTasks([]))
      .finally(() => setLoadingTasks(false))
  }, [visible, activeCatId, job.vesselId])

  return (
    <Modal
      show={visible}
      onHide={onClose}
      size='lg'
      scrollable
      className='pms-nested-above-complete-job'
      backdropClassName='pms-nested-above-complete-job-backdrop'
    >
      <Modal.Header closeButton>
        <Modal.Title>Browse risk assessment library</Modal.Title>
      </Modal.Header>
      <Modal.Body className='text-dark'>
        <p className='text-muted small'>
          Pick a task to fill the risk assessment number on the job. Showing latest version only.
        </p>
        {loadingCats ? (
          <Spinner animation='border' size='sm' />
        ) : (
          <Nav variant='tabs' className='mb-3 flex-nowrap' style={{ overflowX: 'auto' }}>
            {cats.map((c) => (
              <Nav.Item key={c.id}>
                <Nav.Link active={activeCatId === c.id} onClick={() => setActiveCatId(c.id)} className='text-dark'>
                  {c.name}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        )}
        {loadingTasks ? (
          <div className='text-center py-4'>
            <Spinner animation='border' size='sm' />
          </div>
        ) : (
          <div className='table-responsive' style={{ maxHeight: '360px' }}>
            <Table size='sm' bordered hover className='mb-0 align-middle'>
              <thead className='table-light'>
                <tr>
                  <th>RA number</th>
                  <th>Task</th>
                  <th style={{ width: '110px' }}></th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((t) => {
                  const ver = t.taskVersion ?? 0
                  const num = t.categoryTaskNumber ?? 0
                  const disp = formatRiskAssessmentDisplay(t.categoryCode, num, ver)
                  return (
                    <tr key={t.id}>
                      <td className='text-nowrap fw-semibold'>{disp}</td>
                      <td>{t.title}</td>
                      <td>
                        <Button
                          size='sm'
                          variant='light-primary'
                          onClick={() => {
                            onPick(disp)
                            onClose()
                          }}
                        >
                          Use
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
            {!tasks.length && !loadingTasks && <div className='text-muted py-3 text-center'>No tasks in this category.</div>}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
